import type { Template, TemplateDetail, TemplateListResponse } from "@/domain/template";
import { InvalidationEntity } from "../entityDependencies";
import { queryKeys } from "../queryKeys";
import { detailSlot, isObject, listSlot } from "../cache/cacheSlots";
import { defineEntityCache } from "../cache/entityCache";
import {
  byUuid,
  distinctPages,
  listedStatus,
  viewSlot,
  type ViewRules,
  type ViewShape,
} from "../cache/listViews";

export type TemplatePatch = Partial<TemplateDetail>;

const page: ViewShape<TemplateListResponse, Template, string> = {
  isPage: (value): value is TemplateListResponse =>
    isObject(value) && Array.isArray(value.templates) && typeof value.total_count === "number",
  records: (current) => current.templates,
  withRecords: (current, templates, countDelta) => ({
    ...current,
    templates,
    total_count: current.total_count + countDelta,
  }),
  idOf: (record) => record.uuid,
  total: (current) => current.total_count,
};

export const distinctTemplatePages = distinctPages(page);

export const templateRules: ViewRules<Template> = {
  accepts: { status: listedStatus((template) => template.status) },
  narrowing: ["search", "type", "category", "language", "industry", "origin"],
  opaque: { search: (template) => [template.name, template.description] },
  order: {
    updated_at: (template) => (template.updated_at ? Date.parse(template.updated_at) : null),
    created_at: (template) => Date.parse(template.created_at),
  },
  tieBreak: byUuid("asc", (template) => template.uuid),
};

function isTemplateDetail(value: unknown): value is TemplateDetail {
  return isObject(value) && typeof value.uuid === "string" && "content" in value;
}

/** Listed templates carry no document: the content stays in the detail. */
const mergeListed = (
  record: Template,
  { settings: _settings, content: _content, ...listed }: TemplatePatch,
): Template => ({ ...record, ...listed });

export const templateCache = defineEntityCache<string, TemplatePatch, Template>({
  entity: InvalidationEntity.TEMPLATE,
  row: mergeListed,
  slots: (orgId) => [
    viewSlot({
      prefix: queryKeys.templates.lists(orgId),
      shape: page,
      merge: mergeListed,
      parse: queryKeys.templates.parseList,
      rules: templateRules,
    }),
    listSlot({ prefix: queryKeys.templateDropdown.all(orgId), shape: page, merge: mergeListed }),
    detailSlot({
      prefix: queryKeys.templates.details(orgId),
      isRecord: isTemplateDetail,
      idOf: (record) => record.uuid,
      merge: (record: TemplateDetail, patch: TemplatePatch) => ({ ...record, ...patch }),
    }),
  ],
  records: (orgId, uuid) => [queryKeys.templates.detail(orgId, uuid)],
});
